import React, { useState, useEffect } from 'react';
import { authService } from '../services/auth.service';
import { userService } from '../services/user.service';
import { IUser } from '../types';

const Profile: React.FC = () => {
  const [user, setUser] = useState<IUser | null>(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [mfaUpdating, setMfaUpdating] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await authService.getProfile();
      setUser(response.user);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load profile');
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      if (currentPassword) {
        await userService.changePassword(currentPassword, newPassword);
      } else {
        await userService.setPassword(newPassword);
      }
      setMessage('Password updated successfully');
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update password');
    }
  };

  const handleMfaToggle = async () => {
    if (!user) {
      return;
    }
    setError('');
    setMessage('');
    setMfaUpdating(true);
    try {
      const response = await authService.updateMfaSettings(!user.mfaEnabled);
      setUser({ ...user, mfaEnabled: response.mfaEnabled });
      setMessage(`Multi-factor authentication ${response.mfaEnabled ? 'enabled' : 'disabled'}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update MFA settings');
    } finally {
      setMfaUpdating(false);
    }
  };

  if (!user) return <div>Loading...</div>;

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <h2>My Profile</h2>
      {error && <div style={errorStyle}>{error}</div>}
      {message && <div style={successStyle}>{message}</div>}

      <div style={cardStyle}>
        <h3>Personal Information</h3>
        <div style={infoRowStyle}>
          <strong>Name:</strong> {user.firstName} {user.lastName}
        </div>
        <div style={infoRowStyle}>
          <strong>Email:</strong> {user.email}
        </div>
        <div style={infoRowStyle}>
          <strong>Role:</strong> {user.role}
        </div>
      </div>

      <div style={cardStyle}>
        <h3>License Information</h3>
        <div style={infoRowStyle}>
          <strong>Valid Until:</strong> {new Date(user.license.validUntilDate).toLocaleDateString()}
        </div>
      </div>

      <div style={cardStyle}>
        <h3>Password Management</h3>
        {!showPasswordForm ? (
          <button onClick={() => setShowPasswordForm(true)} style={buttonStyle}>
            Change Password
          </button>
        ) : (
          <form onSubmit={handlePasswordChange} style={formStyle}>
            <div style={inputGroupStyle}>
              <label>Current Password (leave empty if none set)</label>
              <div style={passwordContainerStyle}>
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  style={eyeButtonStyle}
                  aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                >
                  {showCurrentPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div style={inputGroupStyle}>
              <label>New Password</label>
              <div style={passwordContainerStyle}>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={eyeButtonStyle}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                >
                  {showNewPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div style={inputGroupStyle}>
              <label>Confirm Password</label>
              <div style={passwordContainerStyle}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={eyeButtonStyle}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="submit" style={buttonStyle}>Update Password</button>
              <button
                type="button"
                onClick={() => setShowPasswordForm(false)}
                style={{ ...buttonStyle, background: '#95a5a6' }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      <div style={cardStyle}>
        <h3>Multi-factor Authentication</h3>
        <p style={{ marginTop: 0, color: '#666' }}>
          Add a verification step when signing in with your password.
        </p>
        <button
          onClick={handleMfaToggle}
          style={buttonStyle}
          disabled={mfaUpdating}
        >
          {user.mfaEnabled ? 'Disable MFA' : 'Enable MFA'}
        </button>
      </div>
    </div>
  );
};

const cardStyle: React.CSSProperties = {
  background: 'white',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
};

const infoRowStyle: React.CSSProperties = {
  padding: '0.75rem 0',
  borderBottom: '1px solid #ecf0f1'
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
};

const inputGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem'
};

const passwordContainerStyle: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center'
};

const inputStyle: React.CSSProperties = {
  padding: '0.75rem',
  paddingRight: '3rem',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '1rem',
  width: '100%'
};

const eyeButtonStyle: React.CSSProperties = {
  position: 'absolute',
  right: '0.75rem',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '0.25rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#666',
  transition: 'color 0.2s'
};

const buttonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  background: '#3498db',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '1rem'
};

const errorStyle: React.CSSProperties = {
  color: '#e74c3c',
  padding: '0.75rem',
  background: '#ffe6e6',
  borderRadius: '4px'
};

const successStyle: React.CSSProperties = {
  color: '#27ae60',
  padding: '0.75rem',
  background: '#e8f8f5',
  borderRadius: '4px'
};

export default Profile;
